import React, { useRef, useEffect, useState } from "react";
import moment from "moment";
import { BryntumWidget } from "@bryntum/schedulerpro-react";
import { Button } from "@/modern/components/ui/button";
import { getImportTypeUndoPreview, undoImport } from "@/network/network";
import { showConfirmationDialog, showToast } from "@/other/utils/utils";
import { WIDGET_KEYS } from "@/other/constants/WidgetKeys";
import { IMPORT_TYPES_HISTORY } from "@/other/constants/OtherKeys";
import {
  cellMenuItemRemoveRow,
} from "@/other/utils/gridCellMenuUtils";
import { pluralize } from "@/other/utils/otherUtils";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

interface HistoryTabProps {
  record: {
    importType: string;
  };
  context: any;
}

export const HistoryTab: React.FC<HistoryTabProps> = ({ record, context }) => {
  const gridRef = useRef<any>(null);
  const { project } = useSwivuBryntumContext();
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  useEffect(() => {
    if (!gridRef.current?.instance || !project) return;

    const grid = gridRef.current.instance;

    // Set up the chained store to filter records
    const importTypeKey = record.importType as keyof typeof IMPORT_TYPES_HISTORY;
    grid.store = project.importBatchStore.chain(
      (storeRecord: any) =>
        storeRecord.entityType === IMPORT_TYPES_HISTORY[importTypeKey] &&
        isValidHistoryRecord(storeRecord)
    );

    // Configure cell menu (processCellMenuItems needs more parameters, skip for now)
    // grid.features.cellMenu.processItems = processCellMenuItems({ grid, user });
  }, [project, record.importType]);

  const isValidHistoryRecord = (record: any) => {
    // Keep only records that have at least one successful import
    return record.recordsImported > 0;
  };

  const toggleContextMenu = (record: any) => {
    if (!gridRef.current?.instance) return;

    const grid = gridRef.current.instance;
    const { undo, removeRow } = grid.features.cellMenu.items;

    // Determine if undo should be disabled based on record state
    const shouldDisableUndo = !record || record.isReverted || record.recordsImported === 0;

    undo.hidden = shouldDisableUndo;
    removeRow.hidden = !shouldDisableUndo;
  };

  const deleteConfirmationDialog = (record: any, msg: string) => {
    if (!gridRef.current?.instance) return;

    showConfirmationDialog({
      title: "Delete Confirmation",
      message: msg,
      cancelBtnText: "Cancel",
      confirmBtnText: "Delete",
      isCloseByHeader: true
    })
      .then((isDeleteConfirmed) => {
        if (isDeleteConfirmed) {
          record.remove();
          showToast({ msg: "Record deleted successfully", type: "success", duration: 3000 });
        }
      })
      .catch(() => {
        // Handle error if needed
      });
  };

  const performUndoAction = (record: any) => {
    if (!gridRef.current?.instance) return;

    getImportTypeUndoPreview(record.id)
      .then(({ data }) => {
        if (data.revertible_records === 0) {
          deleteConfirmationDialog(
            record,
            "All records in this import are changed, there is nothing to undo. <br/> Do you want to delete this record?"
          );
        } else {
          showConfirmationDialog({
            title: `Undoable Records (${data.revertible_records})`,
            message: `<div>There are ${data.revertible_records} undoable ${pluralize(data.revertible_records, "record")}. Do you want to continue?</div>`,
            cancelBtnText: "Cancel",
            confirmBtnText: "Undo",
            isCloseByHeader: true
          }).then((isUndoConfirmed) => {
            if (isUndoConfirmed) {
              // Show async task message dialog for undo import
              const showDialog = shouldShowAsyncDialog("UNDO_CSV_IMPORT");
              if (showDialog && context && context.editorHandler) {
                context.editorHandler.openEditor(
                  EDITOR_KEYS.asyncTaskMessageEditor,
                  null,
                  { record: "UNDO_CSV_IMPORT" },
                  { useExisting: true, by: "recordType" }
                );
              }

              undoImport(record.id)
                .then(() => {
                  showToast({ msg: "Undo Import started successfully...", type: "success", duration: 3000 });
                })
                .catch(() => {
                  showToast({ msg: "Undo failed", type: "error", duration: 3000 });
                });
            }
          });
        }
      })
      .catch(() => {
        showToast({ msg: "Undo failed", type: "error", duration: 3000 });
      });
  };

  const onUndoButtonClick = () => {
    if (selectedRecord) {
      performUndoAction(selectedRecord);
    } else {
      showToast({ msg: "Please select a record to undo", type: "error", duration: 3000 });
    }
  };

  const onRemove = ({ record }: any) => {
    deleteConfirmationDialog(record, "Do you want to delete the import history of this record?");
  };

  const onUndoClick = ({ record }: any) => {
    performUndoAction(record);
  };

  const beforeRenderRow = ({ row, record }: any) => {
    row.cls = { ...row.cls, "opacity-[0.67]": record.isReverted };
  };

  const beforeSelectionChange = ({ selected }: any) => {
    if (selected.length) {
      setSelectedRecord(selected[0]);
      toggleContextMenu(selected[0]);
    } else {
      setSelectedRecord(null);
    }
  };

  const gridConfig = {
    height: "100%",
    listeners: {
      beforeSelectionChange,
      beforeRenderRow
    },
    features: {
      sort: false,
      group: false,
      filter: false,
      headerMenu: false,
      multiSelect: false,
      cellMenu: {
        items: {
          cut: false,
          copy: false,
          paste: false,
          removeRow: cellMenuItemRemoveRow({ onItem: onRemove }),
          undo: {
            text: "Undo Import",
            icon: "b-icon b-icon-undo",
            cls: "b-raised b-red",
            onItem: onUndoClick,
            hidden: false
          }
        }
      }
    },
    columns: [
      {
        id: "file-name",
        field: "fileName",
        text: "File Name",
        flex: 1,
        editor: false,
        htmlEncode: false,
        renderer: ({ record, value }: any) =>
          `<a href="${record.fileUrl}" download onclick="event.stopPropagation();">${value}</a>`
      },
      {
        id: "record-count",
        field: "recordsImported",
        text: "Record Count",
        flex: 1,
        editor: false
      },
      {
        id: "import-date-time",
        field: "importDate",
        text: "Import Date & Time",
        flex: 1,
        editor: false,
        renderer: ({ value }: any) => value ? moment(value).format("MM/DD/YYYY h:mm A") : ""
      },
      {
        id: "imported-by",
        field: "revertedByUserName",
        text: "Imported By",
        flex: 1,
        editor: false
      }
    ]
  };

  const shouldDisableUndo = !selectedRecord || selectedRecord.isReverted || selectedRecord.recordsImported === 0;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden">
        {/* @ts-expect-error bryntum widget throws error on passing config as a prop */}
        <BryntumWidget ref={gridRef} type={WIDGET_KEYS.swivuPreferenceStyleGrid} config={gridConfig} />
      </div>

      <div className="flex items-center justify-start p-4 border-t border-border">
        <Button
          variant="ghost"
          onClick={onUndoButtonClick}
          disabled={shouldDisableUndo}
          className="text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 disabled:opacity-30"
        >
          <i className="fa-solid fa-undo mr-2" />
          Undo Import
        </Button>
      </div>
    </div>
  );
};
